import React from 'react';
import { Link, Redirect } from 'react-router-dom';
import _ from 'lodash';
import i18n from 'i18n';
import {
  faPencilAlt,
  faChartArea,
  faEye,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  OrderListProps,
  OrderListState,
  OrderListColumns,
} from './OrderListModel';
import styles from './OrderList.module.scss';
import { RoleNames } from 'core';
import { hasFuncs, hasRoles, notRoles } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { renderColumn } from 'components/TableColumn/TableColumn';
import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import {
  State,
  OrderCreationInfo,
  OrderCampaignStatus,
} from 'core/order/Order';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import TableRowToolBar from 'containers/TableRowToolBar/TableRowToolBar';

import { Status } from 'components/Status/Status';
import Progress from 'components/Progress/Progress';

import BulkEdit from './BulkEdit/BulkEdit';
import CreateAndFilter from './CreateAndFilter/CreateAndFilter';

export class OrderList extends React.Component<OrderListProps, OrderListState> {
  handler?: number;
  columnFormatterMap: any;
  searchBarRef: any;

  constructor(props) {
    super(props);
    this.searchBarRef = React.createRef();
    this.props.model.setSearchbarRef(this.searchBarRef);
    this.state = this.props.model.state;
    this.columnFormatterMap = {
      [OrderListColumns.NAME]: this.nameFormatter,
      [OrderListColumns.ADVERTISER]: this.dataFormatter,
      [OrderListColumns.APPROVAL]: this.approvalFormatter,
      [OrderListColumns.DAYRANGE]: this.dayRangeFormatter,
      [OrderListColumns.REMAINDAY]: this.dataFormatter,
      [OrderListColumns.PROGRESS]: this.progressFormatter,
      [OrderListColumns.IMPRES]: this.dataFormatter,
      [OrderListColumns.CLICKS]: this.dataFormatter,
      [OrderListColumns.CTR]: this.dataFormatter,
      [OrderListColumns.ROAS]: this.dataFormatter,
      [OrderListColumns.EDITBTNS]: this.floatingEditBtnsFormatter,
    };
  }

  componentDidMount() {
    this.handler = this.props.model.event.add((model) => {
      this.setState(model.state);
    });
    this.props.model.init();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.model !== this.props.model) {
      prevProps.model.event.remove(this.handler);
      this.handler = this.props.model.event.add((model) => {
        this.setState(model.state);
      });
      this.props.model.init();
    }
  }

  componentWillUnmount() {
    this.props.model.onUnmount(this.handler);
  }

  checkboxHeaderFormatter = () => {
    const { selectedOrder, ordersWithPagination } = this.props.model.state;
    const orders = ordersWithPagination?.orders || [];
    return (
      <div className={styles['order-list-checkbox-header']}>
        <input
          type='checkbox'
          checked={selectedOrder.length === orders.length}
          ref={(el) => {
            if (el) {
              el.indeterminate =
                selectedOrder.length > 0 &&
                selectedOrder.length !== orders.length;
            }
          }}
          onChange={this.props.model.handleSelectAll}
          id='order-select-all'
        />
        <label htmlFor='order-select-all' />
      </div>
    );
  };

  checkboxFormatter = (data) => {
    const { orderId } = data;
    const handleSelect = () => {
      this.props.model.handleSelect(orderId);
    };

    const checked = this.props.model.state.selectedOrder.includes(orderId);
    return (
      <div className={styles['order-list-checkbox']}>
        <input
          type='checkbox'
          checked={checked}
          onChange={handleSelect}
          id={`input_${orderId}`}
        />
        <label htmlFor={`input_${orderId}`} />
      </div>
    );
  };

  nameFormatter = (data: any, order: any): any => {
    const model = this.props.model;
    const handleOnClick = () => {
      model.handleCreatorInfoApply(
        _.uniq([...this.state.selectedOrdersToShowInfo, order.id]),
      );
    };
    return (
      <div className={styles.orderCellBox}>
        <div className={`${styles.orderCell} ${styles.nameCell}`}>
          <Link to={`/orders/${data.value}`} className={styles.name}>
            {data.name}
          </Link>
          <div className={styles.orderNumberCell}>{data.orderNumber}</div>
        </div>
        <PermissionChecker permissionAware={hasRoles(RoleNames.agencyAdmin)}>
          <IconWithTooltip
            placement='right'
            className={styles.detailBtn}
            onClick={handleOnClick}
            icon={faEye}
            tooltipProps={{
              id: `orderListCreatorInformation-${data.orderNumber}`,
              tooltip: i18n.t<string>('orderList.labels.creatorInformation'),
            }}
          />
        </PermissionChecker>
      </div>
    );
  };

  approvalFormatter = (data: any, order: any): any => {
    const dangerClass = _.includes(
      [State.NOT_APPROVE, State.CHANGE_PENDING],
      data.value,
    )
      ? styles.danger
      : undefined;
    return (
      <div className={styles.orderCellBox}>
        <div className={`${styles.orderCell} ${dangerClass}`}>{data.desc}</div>
        {this.state.selectedOrdersToShowInfo.includes(order.id) &&
          this.renderCreatorInformation(order)}
      </div>
    );
  };

  dayRangeFormatter = (data: any, order: any) => {
    return (
      <div className={styles.dayRangeCell}>
        <p>{data.startDate}</p>
        {i18n.t<string>('orderList.labels.to')}
        <p>{data.endDate}</p>
      </div>
    );
  };

  dataFormatter = (data: any, row: any): any => {
    return <div className={styles.orderCell}>{data}</div>;
  };

  progressFormatter = (data: any, order: any): any => {
    if (data.tags.length === 0) {
      return (
        <div className={styles.orderCell}>
          {i18n.t<string>('campaignList.labels.emptyContent')}
        </div>
      );
    }

    const colors = {
      [OrderCampaignStatus.AHEAD]: 'danger',
      [OrderCampaignStatus.SEVERELY_BEHIND]: 'deep-warning',
      [OrderCampaignStatus.BEHIND]: 'warning',
      [OrderCampaignStatus.NO_CREATIVES]: 'black',
      [OrderCampaignStatus.NOT_START]: 'ghost',
      [OrderCampaignStatus.NORMAL]: 'success',
      [OrderCampaignStatus.INACTIVE]: 'black',
      [OrderCampaignStatus.END]: 'light',
    };
    return (
      <div className={styles.progressCell}>
        <div className={styles.progressWrap}>
          <div className={styles.progressBar}>
            <Progress
              progress={data.spentStatus.executeRate}
              danger={data.spentStatus.danger}
              deepWarning={data.spentStatus.deepWarning}
              warning={data.spentStatus.warning}
            />
          </div>
          <span>
            {' '}
            {`${i18n.t<string>('orderList.labels.executeRate')} ${(data.spentStatus.executeRate * 100).toFixed(2)}%`}
          </span>
        </div>
        <div>
          {i18n.t<string>('orderList.labels.spent')}: {data.spent}
        </div>
        <div>
          {i18n.t<string>('orderList.labels.budget')}: {data.budget}
        </div>
        <div className={styles.tags}>
          {data.tags.map(({ tag, label }) => (
            <Status key={tag} label={label} color={colors[tag]} />
          ))}
        </div>
      </div>
    );
  };

  floatingEditBtnsFormatter = (cell: any, order: any): any => {
    return (
      <TableRowToolBar className={styles.floatingEditArea}>
        <PermissionChecker permissionAware={hasFuncs(Permission.ORDER_WRITE)}>
          <Link to={`/orders/${order.orderNumber.value}/edit`}>
            <IconWithTooltip
              icon={faPencilAlt}
              tooltipProps={{
                id: `orderListEditTip-${order.id}`,
                tooltip: i18n.t<string>('orderList.labels.editHint'),
              }}
            />
          </Link>
        </PermissionChecker>
        <Link to={this.props.model.getReportPath(order)}>
          <IconWithTooltip
            icon={faChartArea}
            tooltipProps={{
              id: `orderListReportTip-${order.id}`,
              tooltip: i18n.t<string>('orderList.labels.reportHint', {
                reportRangeNote: this.props.model.getReportRangeNote(order),
              }),
            }}
          />
        </Link>
      </TableRowToolBar>
    );
  };

  handleTableChange = (type, props) => {
    this.props.model.handleRemoveSelect();

    if (type === 'sort') {
      this.props.model.sortOrders(props.sortField, props.sortOrder);
      return;
    }
    this.props.model.init(props.page);
  };

  handleOnSearch = (searchString) => {
    this.props.model.handleOnSearch(searchString);
  };

  renderCreatorInformation = (order) => {
    const model = this.props.model;
    const creationInfos = _.map(
      Object.keys(OrderCreationInfo),
      (key) => OrderCreationInfo[key],
    );
    const handleOnClick = () => {
      model.handleCreatorInfoApply(
        this.state.selectedOrdersToShowInfo.filter(
          (selectedId) => selectedId !== order.id,
        ),
      );
    };
    return (
      <div className={styles.creatorInfo}>
        <div className={styles.closeBtn}>
          <FontAwesomeIcon icon={faTimes} onClick={handleOnClick} />
        </div>
        {_.map(creationInfos, (info, index) => (
          <div className={styles.info} key={index}>
            <div className={styles.title}>
              {i18n.t<string>(`orderList.labels.${info}`)}
            </div>
            <div className={styles.content}>{_.get(order, info, 'n/a')}</div>
          </div>
        ))}
      </div>
    );
  };

  render() {
    const model = this.props.model;
    const ordersWithPagination = model.state.ordersWithPagination;
    if (!ordersWithPagination) {
      return <LoadingIndicator />;
    }

    if (model.state.redirectPath) {
      return <Redirect push to={model.state.redirectPath} />;
    }

    const columns = (() => {
      const listColumns = Object.values(OrderListColumns).map(
        (columnToRender) =>
          renderColumn(
            model.getColumnDefinition(columnToRender),
            this.columnFormatterMap[columnToRender],
          ),
      );
      const checkboxColumn = renderColumn(
        model.getColumnDefinition('checkbox', false),
        this.checkboxFormatter,
        this.checkboxHeaderFormatter,
      );
      return [checkboxColumn, ...listColumns];
    })();

    const orderViewData = model.getViewModelData().map((data) => ({
      ...data,
      checkbox: { orderId: data.id, orderNumber: data.orderNumber.value },
    }));

    const rowClasses = (order) => {
      let classes: any = null;
      if (order.needAlert) {
        classes = [classes, styles.lightDanger].join(' ');
      }
      return classes;
    };

    const tableProps = {
      stickFirstColumn: true,
      stickLastColumn: true,
      remote: true,
      keyField: 'id',
      data: orderViewData,
      columns,
      defaultSorted: model.sortDescriptor,
      noDataIndication: i18n.t<string>(model.noDataDescription),
      pagination: {
        page: ordersWithPagination.pagination.page,
        sizePerPage: model.ordersSizePerPage,
        totalSize: ordersWithPagination.pagination.totalCount,
      },
      rowClasses,
      onTableChange: this.handleTableChange,
    };

    const renderTableWithoutAdvertiser = () => {
      const columnsWithoutAdvertiser = columns.filter(
        (column) => column.dataField !== OrderListColumns.ADVERTISER,
      );
      return (
        <StickableBootstrapTable
          {...tableProps}
          columns={columnsWithoutAdvertiser}
        />
      );
    };
    return (
      <div className={styles.orderListContainer}>
        {model.state.loading && <LoadingIndicator />}
        <div className={styles.operationArea}>
          {model.state.selectedOrder.length ? (
            <BulkEdit
              selectedItems={model.state.selectedOrder}
              onRemoveSelect={model.handleRemoveSelect}
              onActivate={model.bulkActivateOrders}
              onDeactivate={model.bulkDeactivateOrders}
              onApprove={model.bulkApproveOrders}
              onReject={model.bulkRejectOrders}
            />
          ) : (
            <CreateAndFilter
              searchBarRef={this.searchBarRef}
              filterMenuTabConfigs={model.filterMenuTabConfigs}
              onSearch={this.handleOnSearch}
              searchString={model.searchString}
            />
          )}
        </div>
        <div className={styles.orderList}>
          <PermissionChecker
            permissionAware={notRoles(
              RoleNames.adsAdmin,
              RoleNames.adsSales,
              RoleNames.adsReport,
            )}
            renderOtherwise={renderTableWithoutAdvertiser}
          >
            <StickableBootstrapTable {...tableProps} />
          </PermissionChecker>
        </div>
      </div>
    );
  }
}
